import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import {
  BookOpen,
  ChevronLeft,
  ChevronRight,
  GraduationCap,
  Handshake,
  Heart,
  Mic,
  Network,
  Shield,
  Users,
  X,
} from 'lucide-react';
import './App.css';
import './styles/civic.css';
import Header from './components/Header';
import Navigation from './components/Navigation';
import Footer from './components/Footer';
import MobileStickyCta from './components/MobileStickyCta';
import FlyerModal from './components/FlyerModal';
import SectionHeader from './components/SectionHeader';
import OfficialFigures from './components/OfficialFigures';
import ProgramList from './components/ProgramList';
import PrincipleList from './components/PrincipleList';
import CumbreNotice from './components/CumbreNotice';
import CumbreRuta from './components/CumbreRuta';
import ContactBlock from './components/ContactBlock';
import { CUMBRE_RUTA } from './constants/cumbreRuta';
import { INLINE } from './generated/inlineImages';
import { gallerySrc } from './utils/galleryUrl';

const fadeInUp = {
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
};

const transitionConfig = { duration: 0.55, ease: 'easeOut' as const };

const whatWeDo = [
  {
    name: 'Contenido Digital',
    description:
      'Creamos contenido informativo y de opinión sobre los principales temas de la batalla cultural y política a nivel nacional e internacional.',
    icon: <Mic size={22} />,
  },
  {
    name: 'Producción de Eventos',
    description:
      'Generamos encuentros de networking y de capacitación para que puedas ser una voz diferente en tu área de influencia.',
    icon: <Users size={22} />,
  },
  {
    name: 'Cumbre Ciudadano y Creyente',
    description: 'El primer encuentro de jóvenes cristianos políticos alrededor de todo el Perú.',
    icon: <Handshake size={22} />,
  },
  {
    name: 'Una red nacional en construcción',
    description:
      'Cada encuentro conecta a pastores, autoridades, profesionales, jóvenes y servidores públicos de diferentes regiones.',
    icon: <Network size={22} />,
  },
  {
    name: 'Formación para la vida pública',
    description:
      'Las cumbres ofrecen conferencias, paneles y espacios de articulación sobre cosmovisión bíblica, gobierno, liderazgo e incidencia pública.',
    icon: <GraduationCap size={22} />,
  },
];

const values = [
  {
    name: 'Vida',
    description: 'Somos pro-vida en todas sus etapas, desde la concepción hasta la muerte natural',
    icon: <Heart size={20} />,
  },
  {
    name: 'Familia',
    description:
      'Reconocemos a la familia como la base fundamental de la sociedad y creemos que esta debe ser liderada por un matrimonio entre hombre y mujer',
    icon: <Users size={20} />,
  },
  {
    name: 'Libertad',
    description: 'Defendemos la libertad como derecho fundamental e inherente de toda persona',
    icon: <Shield size={20} />,
  },
  {
    name: 'Verdad',
    description: 'Hay una sola verdad, y esta es Jesús.',
    icon: <BookOpen size={20} />,
  },
];

const ABOUT_PHOTOS = CUMBRE_RUTA.flatMap(({ photo }) => (photo ? [photo] : []));

function App() {
  const logo = INLINE.logoUvd;
  const [showFlyerModal, setShowFlyerModal] = useState(false);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [isCarouselPaused, setIsCarouselPaused] = useState(false);
  const [lightboxPhoto, setLightboxPhoto] = useState<number | null>(null);
  const photosPerView = 4;
  const totalPhotos = 33;

  useEffect(() => {
    const isMobileViewport = window.matchMedia?.('(max-width: 767px)')?.matches === true;
    if (isMobileViewport) {
      return;
    }
    const timer = window.setTimeout(() => setShowFlyerModal(true), 1000);
    return () => window.clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (isCarouselPaused) return;
    const autoPlayInterval = setInterval(() => {
      setCurrentPhotoIndex((prev) => {
        const maxIndex = Math.max(0, totalPhotos - photosPerView);
        return prev >= maxIndex ? 0 : prev + 1;
      });
    }, 3000);
    return () => clearInterval(autoPlayInterval);
  }, [isCarouselPaused]);

  const goToNextPhotos = () => {
    setIsCarouselPaused(true);
    setCurrentPhotoIndex((prev) => {
      const maxIndex = Math.max(0, totalPhotos - photosPerView);
      return prev >= maxIndex ? 0 : prev + 1;
    });
    setTimeout(() => setIsCarouselPaused(false), 5000);
  };

  const goToPreviousPhotos = () => {
    setIsCarouselPaused(true);
    setCurrentPhotoIndex((prev) => {
      const maxIndex = Math.max(0, totalPhotos - photosPerView);
      return prev <= 0 ? maxIndex : prev - 1;
    });
    setTimeout(() => setIsCarouselPaused(false), 5000);
  };

  const allPhotos = Array.from({ length: totalPhotos }, (_, index) => index + 1);

  return (
    <div className="App">
      <a href="#main-content" className="skip-link">
        Saltar al contenido principal
      </a>

      <FlyerModal open={showFlyerModal} onClose={() => setShowFlyerModal(false)} />

      <Navigation logo={logo} />
      <Header logo={logo} />

      <main className="main" id="main-content">
        <motion.section
          id="quienes-somos"
          className="section section--about"
          initial={fadeInUp.initial}
          whileInView={fadeInUp.animate}
          transition={transitionConfig}
          viewport={{ once: true }}
        >
          <SectionHeader
            index="01"
            eyebrow="Institución"
            title="Quiénes Somos"
            description="Una Voz Diferente (UVD) es una organización juvenil peruana sin fines de lucro. Promovemos el ejercicio responsable de la ciudadanía y el liderazgo con principios desde una cosmovisión cristiana."
          />
          <div className="about__content">
            <div className="about__photos">
              {ABOUT_PHOTOS.map((photo) => (
                <figure key={photo.caption} className="about__photo">
                  <img
                    src={photo.src}
                    alt={photo.alt}
                    className="about__photo-image"
                    loading="lazy"
                  />
                  <figcaption className="about__photo-caption">{photo.caption}</figcaption>
                </figure>
              ))}
            </div>
            <div className="about__ledger">
              <OfficialFigures />
            </div>
          </div>
        </motion.section>

        <motion.section
          id="que-hacemos"
          className="section section--what-we-do"
          initial={fadeInUp.initial}
          whileInView={fadeInUp.animate}
          transition={transitionConfig}
          viewport={{ once: true }}
        >
          <SectionHeader
            index="02"
            eyebrow="Programas"
            title="¿Qué hacemos?"
            description="Una Voz Diferente desarrolla múltiples actividades para formar líderes comprometidos con la transformación de la sociedad, bajo dos ejes de acción principales y un evento sello."
          />
          <ProgramList items={whatWeDo} />
        </motion.section>

        <motion.section
          id="valores"
          className="section section--values"
          initial={fadeInUp.initial}
          whileInView={fadeInUp.animate}
          transition={transitionConfig}
          viewport={{ once: true }}
        >
          <SectionHeader
            index="03"
            eyebrow="Principios"
            title="Nuestros Valores"
            description="Los principios fundamentales que guían nuestro trabajo y forman la base de nuestro liderazgo."
          />
          <PrincipleList items={values} />
        </motion.section>

        <motion.section
          id="galeria"
          className="section section--gallery"
          initial={fadeInUp.initial}
          whileInView={fadeInUp.animate}
          transition={transitionConfig}
          viewport={{ once: true }}
        >
          <SectionHeader
            index="04"
            eyebrow="Archivo fotográfico"
            title="Galería"
            description="Momentos especiales de nuestros eventos y actividades que han marcado nuestra trayectoria."
          />

          <div className="gallery__container">
            <div className="gallery__wrapper">
              <button
                className="gallery__nav gallery__nav--prev"
                onClick={goToPreviousPhotos}
                aria-label="Fotos anteriores"
                type="button"
              >
                <ChevronLeft size={22} />
              </button>

              <div className="gallery__carousel-container">
                <motion.div
                  className="gallery__track"
                  style={{ '--gallery-count': totalPhotos } as React.CSSProperties}
                  animate={{ x: `-${(currentPhotoIndex / totalPhotos) * 100}%` }}
                  transition={{ duration: 0.55, ease: 'easeInOut' }}
                >
                  {allPhotos.map((photoNumber) => (
                    <button
                      type="button"
                      key={photoNumber}
                      className="gallery__item"
                      onClick={() => setLightboxPhoto(photoNumber)}
                      aria-label={`Abrir imagen ${photoNumber} de la galería`}
                    >
                      <div className="gallery__image-wrapper">
                        <img
                          src={gallerySrc(`imagen${photoNumber}.webp`)}
                          alt={`Imagen ${photoNumber} de la galería`}
                          className="gallery__image"
                          loading="lazy"
                        />
                        <span className="gallery__caption">
                          Archivo · {String(photoNumber).padStart(2, '0')}
                        </span>
                      </div>
                    </button>
                  ))}
                </motion.div>
              </div>

              <button
                className="gallery__nav gallery__nav--next"
                onClick={goToNextPhotos}
                aria-label="Fotos siguientes"
                type="button"
              >
                <ChevronRight size={22} />
              </button>
            </div>

            <div className="gallery__indicators">
              {Array.from({ length: Math.ceil(totalPhotos / photosPerView) }, (_, index) => (
                <button
                  key={index}
                  type="button"
                  className={`gallery__indicator ${
                    Math.floor(currentPhotoIndex / photosPerView) === index
                      ? 'gallery__indicator--active'
                      : ''
                  }`}
                  onClick={() => {
                    setIsCarouselPaused(true);
                    setCurrentPhotoIndex(index * photosPerView);
                    setTimeout(() => setIsCarouselPaused(false), 5000);
                  }}
                  aria-label={`Ir al grupo ${index + 1} de la galería`}
                />
              ))}
            </div>
          </div>
        </motion.section>

        <motion.section
          id="slogan"
          className="section section--slogan"
          initial={fadeInUp.initial}
          whileInView={fadeInUp.animate}
          transition={transitionConfig}
          viewport={{ once: true }}
        >
          <blockquote className="slogan__quote">
            <p>
              "Damos la batalla cultural y la batalla política sin perder de vista la guerra
              espiritual."
            </p>
          </blockquote>
        </motion.section>

        <motion.section
          id="cumbre-cyc"
          className="section section--cumbre"
          initial={fadeInUp.initial}
          whileInView={fadeInUp.animate}
          transition={transitionConfig}
          viewport={{ once: true }}
        >
          <CumbreNotice />
        </motion.section>

        <motion.section
          id="ruta-nacional"
          className="section section--ruta"
          initial={fadeInUp.initial}
          whileInView={fadeInUp.animate}
          transition={transitionConfig}
          viewport={{ once: true }}
        >
          <SectionHeader index="06" eyebrow="Ruta" title="Ruta nacional" />
          <CumbreRuta />
        </motion.section>

        <motion.section
          id="contacto"
          className="section section--contact"
          initial={fadeInUp.initial}
          whileInView={fadeInUp.animate}
          transition={transitionConfig}
          viewport={{ once: true }}
        >
          <SectionHeader
            index="07"
            eyebrow="Atención"
            title="Contáctanos"
            description="Estamos aquí para responder tus preguntas y conocerte mejor. ¡Ponte en contacto con nosotros!"
          />

          <ContactBlock />
        </motion.section>
      </main>

      <AnimatePresence>
        {lightboxPhoto !== null && (
          <Dialog
            open
            onClose={() => setLightboxPhoto(null)}
            maxWidth="md"
            fullWidth
            aria-labelledby="gallery-lightbox-title"
            slotProps={{
              backdrop: {
                sx: { backgroundColor: 'rgba(26, 35, 50, 0.82)' },
              },
              paper: {
                sx: { backgroundColor: '#1A2332', overflow: 'hidden' },
              },
            }}
          >
            <h2 id="gallery-lightbox-title" className="sr-only">
              Imagen {lightboxPhoto} de la galería
            </h2>
            <IconButton
              onClick={() => setLightboxPhoto(null)}
              aria-label="Cerrar imagen"
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                zIndex: 2,
                color: '#FAFAF7',
                backgroundColor: 'rgba(26, 35, 50, 0.45)',
              }}
            >
              <X size={20} />
            </IconButton>
            <img
              src={gallerySrc(`imagen${lightboxPhoto}.webp`)}
              alt={`Imagen ${lightboxPhoto} de la galería`}
              className="gallery-lightbox__image"
            />
          </Dialog>
        )}
      </AnimatePresence>

      <MobileStickyCta />
      <Footer />
    </div>
  );
}

export default App;
